import { useState, useEffect, useCallback } from "react";
import { FaSave, FaPrint, FaTrash, FaMoneyBillWave, FaUserMd } from "react-icons/fa";
import axios from "axios";
import { toast } from "react-toastify";
import { HiXMark } from "react-icons/hi2";
import ConfirmActionModal from "../../ConfirmDeleteModal";
import DynamicSelect from "../../DynamicSelect";
import { PrintBillLayoutA5 } from "../../PrintLayoutA5";

const HospitalAddPaymentModal = ({
  isOpen,
  onClose,
  bill: initialBill,
  onPaymentSuccess,
}) => {
  const [billDetails, setBillDetails] = useState(initialBill);
  const [transactions, setTransactions] = useState(initialBill?.payments || []);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [doctors, setDoctors] = useState([]);
  
  const [confirmDelete, setConfirmDelete] = useState({ show: false, paymentId: null });
  const [printData, setPrintData] = useState(null);
  const [showPrintPreview, setShowPrintPreview] = useState(false);

  const [patientForm, setPatientForm] = useState({
    date: new Date().toISOString().split("T")[0],
    amount: "",
    discount: "",
    paymentMode: "Cash",
    note: "",
  });

  const [doctorForm, setDoctorForm] = useState({
    doctorId: null,
    amount: "",
    note: "",
    date: new Date().toISOString().split("T")[0],
  });

  // API Configuration
  const API_KEY = "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";
  const BASE_URL = "https://api.muktihospital.com/api";

  // 1. Fetch Doctors List
  useEffect(() => {
    const fetchDoctors = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/doctor`, { headers: { "x-api-key": API_KEY } });
        let rawData = [];
        if (Array.isArray(res.data)) rawData = res.data;
        else if (res.data?.doctors) rawData = res.data.doctors;
        else if (res.data?.data) rawData = res.data.data;

        const options = rawData.map(d => {
             let name = d.name || "Unknown";
             if(d.translations) {
                 try {
                     const t = typeof d.translations === 'string' ? JSON.parse(d.translations) : d.translations;
                     name = t?.en?.name || t?.bn?.name || name;
                 } catch(e){}
             }
             return { value: d.id, label: name + (d.specialization ? ` (${d.specialization})` : '') };
        });
        setDoctors(options);
      } catch (error) { console.error(error); }
    };
    if (isOpen) fetchDoctors();
  }, [isOpen]);

  // 2. Fetch Latest Bill Data (Hospital API)
  const fetchLatestData = useCallback(async () => {
    if (!initialBill?.id) return;
    setIsLoading(true);
    try {
      // Endpoint: /api/hospital/hospital-bills/:id
      const res = await axios.get(`${BASE_URL}/hospital/hospital-bills/${initialBill.id}`, {
        headers: { "x-api-key": API_KEY },
      });
      const fullData = res.data.bill;
      setBillDetails(fullData);
      setTransactions(fullData.payments || []);
      
      const due = (fullData.netAmount || 0) - (fullData.paidAmount || 0);
      setPatientForm(prev => ({ ...prev, amount: due > 0 ? due : "" }));
    } catch (error) { console.error(error); } 
    finally { setIsLoading(false); }
  }, [initialBill?.id]);

  useEffect(() => {
    if (isOpen) {
        setBillDetails(initialBill);
        setTransactions(initialBill?.payments || []);
        fetchLatestData();
    }
  }, [isOpen, initialBill, fetchLatestData]);

  // 3. Submit Patient Payment
  const handlePatientSubmit = async (e) => {
    e.preventDefault();
    const remainingDue = (billDetails?.netAmount || 0) - (billDetails?.paidAmount || 0);
    if (parseFloat(patientForm.amount || 0) > remainingDue && remainingDue > 0) {
         toast.warning("Warning: Payment exceeds due amount.");
    }
    
    setIsSubmitting(true);
    try {
      const payload = {
        billId: billDetails.id,
        paymentDate: patientForm.date,
        amount: parseFloat(patientForm.amount || 0),
        discount: parseFloat(patientForm.discount || 0),
        paymentMode: patientForm.paymentMode,
        note: patientForm.note,
        doctorId: null,
        doctorAmount: null,
        doctorNote: null
      };

      await axios.post(`${BASE_URL}/hospital/hospital-bills/add-payment`, payload, { headers: { "x-api-key": API_KEY } });
      toast.success("Patient payment received!");
      if (onPaymentSuccess) onPaymentSuccess();
      await fetchLatestData();

      setPatientForm(prev => ({ ...prev, amount: "", discount: "", note: "" }));
    } catch (error) {
      toast.error("Failed to save patient payment.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // 4. Submit Doctor Payment
  const handleDoctorSubmit = async (e) => {
    e.preventDefault();
    if (!doctorForm.doctorId || !doctorForm.amount) {
        return toast.warning("Please select a doctor and amount.");
    }

    setIsSubmitting(true);
    try {
      const payload = {
        billId: billDetails.id,
        paymentDate: doctorForm.date,
        amount: 0,
        discount: 0, 
        paymentMode: "Cash", 
        note: "Doctor Payment", 
        doctorId: doctorForm.doctorId,
        doctorAmount: parseFloat(doctorForm.amount),
        doctorNote: doctorForm.note
      };

      // Endpoint: /api/hospital/hospital-bills/add-payment
      await axios.post(`${BASE_URL}/hospital/hospital-bills/add-payment`, payload, { headers: { "x-api-key": API_KEY } });
      toast.success("Doctor paid successfully!");
      await fetchLatestData();
      setDoctorForm(prev => ({ ...prev, amount: "", note: "" })); 
    } catch (error) {
      toast.error("Failed to pay doctor.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Print Logic (same as HospitalBillModal)
  useEffect(() => {
    if (showPrintPreview && printData) {
      const timer = setTimeout(() => {
        const elementsToHide = document.querySelectorAll(".print-hidden, .print-hidden-in-print");
        elementsToHide.forEach((el) => (el.style.display = "none"));

        const printLayout = document.querySelector(".payment-history-print-preview");
        if (printLayout) {
          printLayout.style.display = "block";
        }

        window.print();

        setTimeout(() => {
          elementsToHide.forEach((el) => (el.style.display = ""));
          if (printLayout) {
            printLayout.style.display = "none";
          }
          setShowPrintPreview(false);
          setPrintData(null);
        }, 500);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [showPrintPreview, printData]);

  // Delete Transaction Logic
  const initiateDelete = (paymentId) => setConfirmDelete({ show: true, paymentId });
  const executeDelete = async () => {
    if (!confirmDelete.paymentId) return;
    try {
      await axios.delete(`${BASE_URL}/hospital/hospital-bills/delete-payment/${confirmDelete.paymentId}`, {
         headers: { "x-api-key": API_KEY } 
      });
      toast.success("Transaction deleted!");
      if (onPaymentSuccess) onPaymentSuccess();
      await fetchLatestData();
      setConfirmDelete({ show: false, paymentId: null });
    } catch (error) {
      toast.error("Failed to delete transaction.");
    }
  };

  const numberToWordsBD = (value) => {
    let num = Math.floor(Number(value) || 0);
    if (num === 0) return "Zero";
    const ones = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
    const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
    const two = (n) => (n < 20 ? ones[n] : tens[Math.floor(n / 10)] + (n % 10 ? " " + ones[n % 10] : ""));
    const three = (n) => {
      const h = Math.floor(n / 100);
      const r = n % 100;
      return (h ? ones[h] + " Hundred" + (r ? " " : "") : "") + (r ? two(r) : "");
    };
    let words = "";
    const crore = Math.floor(num / 10000000); num %= 10000000;
    const lakh = Math.floor(num / 100000); num %= 100000;
    const thousand = Math.floor(num / 1000); num %= 1000;
    if (crore) words += three(crore) + " Crore ";
    if (lakh) words += two(lakh) + " Lakh ";
    if (thousand) words += two(thousand) + " Thousand ";
    if (num) words += three(num);
    return words.trim();
  };

  const handlePrintReceipt = () => {
    const activeBill = billDetails || initialBill || {};
    const services = activeBill.serviceItems || [];
    const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-GB") : "N/A");

    const signatureFooter = (
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "0px", paddingRight: "6px" }}>
        <div style={{ textAlign: "center", minWidth: "180px" }}>
          <p style={{ margin: "0 0 2px 0", fontWeight: "bold", fontSize: "9.5pt", color: "#323290", fontFamily: "cursive" }}>
            {activeBill.authorizedBy || "N/A"}
          </p>
          <div style={{ borderTop: "1.5px solid #323290", width: "160px", margin: "0 auto 3px auto" }}></div>
          <p style={{ margin: "0", fontSize: "8pt", fontWeight: "bold", letterSpacing: "0.5px", textTransform: "uppercase", color: "#374151" }}>Authorized By</p>
          <p style={{ margin: "1px 0 0 0", fontSize: "7pt", color: "#6b7280" }}>Mukti Hospital Pvt. Ltd.</p>
        </div>
      </div>
    );

    const billInfo = (
      <>
        <div>
          <p><strong>Bill No:</strong> {activeBill.billNumber || "N/A"}</p>
          <p><strong>Special No:</strong> {activeBill.specialNo || "N/A"}</p>
          <p><strong>Reg No:</strong> {activeBill.regNo || "N/A"}</p>
        </div>
        <div>
          <p><strong>Admission Date:</strong> {fmtDate(activeBill.admissionDate)}</p>
          <p><strong>Billing Date:</strong> {fmtDate(activeBill.billingDate)}</p>
        </div>
        <p className="full"><strong>Patient Name:</strong> {activeBill.patientName || "N/A"}</p>
        <p className="full"><strong>Consultant:</strong> {activeBill.consultantName || "N/A"}</p>
      </>
    );

    const table = (
      <table className="w-full border-collapse text-base">
        <thead>
          <tr className="bg-gray-100">
            <th className="p-2 border font-semibold">Sl No</th>
            <th className="p-2 border font-semibold">Service Name</th>
            <th className="p-2 border font-semibold text-right">Unit Price (৳)</th>
            <th className="p-2 border font-semibold text-right">Total Unit</th>
            <th className="p-2 border font-semibold text-right">Amount (৳)</th>
          </tr>
        </thead>
        <tbody>
          {services.map((row, index) => (
            <tr key={index} className="border-t">
              <td className="p-2 border">{index + 1}</td>
              <td className="p-2 border">{row.serviceName || "N/A"}</td>
              <td className="p-2 border text-right">{parseFloat(row.unitPrice || 0).toFixed(2)}</td>
              <td className="p-2 border text-right">{row.totalUnit || "0"}</td>
              <td className="p-2 border text-right">{parseFloat(row.amount || 0).toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );

    const billAmount = services.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
    const discount = Number(activeBill.discountAmount ?? activeBill.discount ?? 0);
    const netAmount = Number(activeBill.netAmount || 0);
    const paidAmount = Number(activeBill.paidAmount || 0);
    const payable = netAmount - paidAmount;

    const summary = (
      <div className="flex flex-col text-xl">
        <div className="flex justify-between text-gray-600"><span>Bill Amount:</span><span>{Math.round(billAmount).toFixed(2)}</span></div>
        <div className="flex justify-between text-gray-600"><span>Discount:</span><span>{Math.round(discount).toFixed(2)}</span></div>
        <div className="flex justify-between text-gray-600"><span>Net Amount:</span><span>{Math.round(netAmount).toFixed(2)}</span></div>
        <div className="flex justify-between text-gray-600"><span>Paid Amount:</span><span>{Math.round(paidAmount).toFixed(2)}</span></div>
        <div className="flex justify-between"><span className="text-xl">Payable Amount:</span><span className="text-xl">{Math.round(payable).toFixed(2)}</span></div>
      </div>
    );

    setPrintData({ billInfo, table, summary, signatureFooter, title: "Hospital Bill" });
    setShowPrintPreview(true);
  };

  if (!isOpen) return null;

  const activeBill = billDetails || initialBill || {};
  const patient = activeBill.patient || {};
  const currentTotalAmount = activeBill.totalAmount || 0;
  const currentPaid = activeBill.paidAmount || 0;
  const currentDue = (activeBill.netAmount || 0) - currentPaid;

  const InfoRow = ({ label, value, isBold }) => (
    <div className="flex justify-between border-b border-gray-100 py-2 text-sm last:border-0">
      <span className="text-gray-500 font-medium">{label}</span>
      <span className={`text-right text-gray-800 ${isBold ? "font-bold" : ""}`}>{value || "-"}</span>
    </div>
  );

  return (
    <>
      <div className="fixed inset-0 z-40 flex items-start sm:items-center justify-center bg-black bg-opacity-40 px-4 sm:px-0 overflow-y-auto print-hidden">
        <div className="bg-white shadow-lg w-full my-4 sm:my-0 max-h-[100vh] h-full overflow-y-auto">
          
          {/* Header */}
          <div className="flex items-center justify-between bg-gray-200 p-4 border-b border-gray-300">
             <h2 className="text-lg font-bold">Manage Hospital Payments</h2>
             <button onClick={onClose} className="size-7 rounded-full bg-white text-M-secondary-color text-sm inline-flex items-center justify-center hover:bg-M-secondary-color hover:text-white transition-all duration-300"><HiXMark/></button>
          </div>

          <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              
              {/* LEFT: Bill Details */}
              <div className="lg:col-span-4 space-y-4">
                <div className="bg-white p-5 rounded-lg shadow-sm border border-gray-200">
                  <h3 className="text-gray-700 font-bold border-b pb-2 mb-3 text-sm uppercase">Bill Summary</h3>
                  <InfoRow label="Bill No" value={activeBill.billNumber} isBold />
                  <InfoRow label="Total Bill" value={`৳ ${currentTotalAmount}`} />
                  <InfoRow 
                    label="Total Discount" 
                    value={`৳ ${(activeBill.discount || 0).toLocaleString()}`} 
                    className="text-blue-600" 
                  />
                  <InfoRow label="Total Paid" value={`৳ ${currentPaid}`} isBold/>
                  <div className="flex justify-between py-2 text-sm font-bold text-red-600 border-t mt-2">
                      <span>Due Amount</span>
                      <span>৳ {currentDue}</span>
                  </div>
                </div>
                <div className="bg-white p-5 rounded-lg shadow-sm border border-gray-200">
                  <h3 className="text-gray-700 font-bold border-b pb-2 mb-3 text-sm uppercase">Patient Information</h3>
                  <InfoRow label="Patient Name" value={activeBill.patientName} />
                  <InfoRow label="Reg No" value={activeBill.regNo} />
                  <InfoRow label="Age" value={patient.age ? `${patient.age} Y` : "N/A"} />
                  <InfoRow label="Gender" value={patient.gender || activeBill.gender} />
                  <InfoRow label="Blood Group" value={patient.bloodGroup || activeBill.bloodGroup} />
                  <InfoRow label="Mobile" value={patient.phoneNumber || activeBill.phoneNumber} />
                  <InfoRow label="Email" value={patient.email || activeBill.email} />
                  <InfoRow label="Address" value={patient.address || activeBill.address} />
                </div>
              </div>

              {/* RIGHT: TWO FORMS */}
              <div className="lg:col-span-8 space-y-6">
                
                {/* 1. PATIENT PAYMENT (GREEN) */}
                <div className="bg-white p-5 rounded-lg shadow-sm border-l-4 border-[#73CB64]">
                   <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2 border-b pb-2">
                       <FaMoneyBillWave className="text-[#73CB64]"/> Receive Patient Payment
                   </h3>
                   <form onSubmit={handlePatientSubmit}>
                       <div className="grid grid-cols-3 gap-3 mb-3">
                           <div>
                               <label className="text-xs font-bold block mb-1">Date</label>
                               <input type="date" value={patientForm.date} onChange={e=>setPatientForm({...patientForm, date: e.target.value})} className="w-full border p-2 rounded text-sm"/>
                           </div>
                           <div>
                               <label className="text-xs font-bold block mb-1">Amount (৳)</label>
                               <input type="number" placeholder="0.00" value={patientForm.amount} onChange={e=>setPatientForm({...patientForm, amount: e.target.value})} className="w-full border p-2 rounded text-sm focus:border-[#73CB64] outline-none"/>
                           </div>
                           <div>
                               <label className="text-xs font-bold block mb-1">Discount (৳)</label>
                               <input type="number" placeholder="0.00" value={patientForm.discount} onChange={e=>setPatientForm({...patientForm, discount: e.target.value})} className="w-full border p-2 rounded text-sm focus:border-[#73CB64] outline-none"/>
                           </div>
                       </div>
                       <div className="flex gap-3 mb-3">
                           <div className="flex-1">
                               <label className="text-xs font-bold block mb-1">Mode</label>
                               <select value={patientForm.paymentMode} onChange={e=>setPatientForm({...patientForm, paymentMode: e.target.value})} className="w-full border p-2 rounded text-sm">
                                   <option>Cash</option><option>Card</option><option>Mobile Banking</option>
                               </select>
                           </div>
                           <div className="flex-[2]">
                               <label className="text-xs font-bold block mb-1">Note</label>
                               <input type="text" value={patientForm.note} onChange={e=>setPatientForm({...patientForm, note: e.target.value})} className="w-full border p-2 rounded text-sm"/>
                           </div>
                       </div>
                       <div className="text-right">
                           <button type="submit" disabled={isSubmitting} className="bg-[#73CB64] text-white px-5 py-2 rounded text-sm hover:bg-M-primary-color font-medium shadow-sm transition">
                              {isSubmitting ? "..." : "Receive Payment"}
                           </button>
                       </div>
                   </form>
                </div>

                {/* 2. DOCTOR PAYMENT (PURPLE) */}
                <div className="bg-white p-5 rounded-lg shadow-sm border-l-4 border-M-primary-color/90">
                   <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2 border-b pb-2">
                       <FaUserMd className="text-M-primary-color/90"/> Pay Doctor (Commission/Fee)
                   </h3>
                   <form onSubmit={handleDoctorSubmit}>
                       <div className="mb-3">
                           <label className="text-xs font-bold block mb-1">Select Doctor</label>
                           <DynamicSelect 
                               options={doctors}
                               value={doctors.find(d => d.value === doctorForm.doctorId)}
                               onChange={(opt) => setDoctorForm({...doctorForm, doctorId: opt?.value || null})}
                               placeholder="Search Doctor..."
                               isClearable
                           />
                       </div>
                       <div className="grid grid-cols-2 gap-3 mb-3">
                           <div>
                               <label className="text-xs font-bold block mb-1">Pay Amount (৳)</label>
                               <input type="number" placeholder="0.00" value={doctorForm.amount} onChange={e=>setDoctorForm({...doctorForm, amount: e.target.value})} className="w-full border p-2 rounded text-sm focus:border-M-primary-color/90 outline-none"/>
                           </div>
                           <div>
                               <label className="text-xs font-bold block mb-1">Reason / Note</label>
                               <input type="text" placeholder="e.g. Commission" value={doctorForm.note} onChange={e=>setDoctorForm({...doctorForm, note: e.target.value})} className="w-full border p-2 rounded text-sm"/>
                           </div>
                       </div>
                       <div className="text-right">
                           <button type="submit" disabled={isSubmitting || !doctorForm.doctorId} className="bg-M-primary-color/90 text-white px-5 py-2 rounded text-sm hover:bg-M-primary-color font-medium shadow-sm transition disabled:opacity-50">
                               {isSubmitting ? "..." : "Pay Doctor"}
                           </button>
                       </div>
                   </form>
                </div>

              </div>
            </div>

            {/* TRANSACTION HISTORY TABLE */}
            <div className="mt-8 bg-white p-5 rounded-lg shadow-sm border border-gray-200">
              <h3 className="text-gray-800 font-bold border-b pb-3 mb-4 text-sm uppercase">Transaction History</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left border border-gray-200">
                  <thead className="bg-gray-100 text-gray-600 uppercase text-xs">
                    <tr>
                      <th className="px-4 py-3 font-semibold border-b">ID</th>
                      <th className="px-4 py-3 font-semibold border-b">Date</th>
                      <th className="px-4 py-3 font-semibold border-b">Details</th>
                      <th className="px-4 py-3 font-semibold border-b text-right">Amount (৳)</th>
                      <th className="px-4 py-3 font-semibold border-b text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 text-gray-700">
                    {transactions.length > 0 ? transactions.map((tx, idx) => {
                       const isDocPay = tx.doctorAmount > 0;
                       
                       let docName = "Doctor";
                       if(tx.doctor?.translations) {
                           try {
                               const t = typeof tx.doctor.translations === 'string' ? JSON.parse(tx.doctor.translations) : tx.doctor.translations;
                               docName = t?.en?.name || docName;
                           } catch(e){}
                       } else if (tx.doctor?.name) {
                           docName = tx.doctor.name;
                       }

                       return (
                          <tr key={idx} className={`hover:bg-gray-50 ${isDocPay ? 'bg-purple-50/40' : ''}`}>
                            <td className="px-4 py-3 font-mono text-xs text-gray-500">{tx.id ? `PAY-${tx.id}` : '-'}</td>
                            <td className="px-4 py-3 whitespace-nowrap">{new Date(tx.paymentDate).toLocaleDateString("en-GB")}</td>
                            
                            {/* DETAILS COLUMN */}
                            <td className="px-4 py-3">
                                {isDocPay ? (
                                    <div className="flex flex-col">
                                        <span className="text-xs font-bold text-M-primary-color/90 flex items-center gap-1">
                                            <FaUserMd size={10}/> Paid to: {docName}
                                        </span>
                                        {tx.doctorNote && <span className="text-[10px] text-gray-500 italic mt-0.5">{tx.doctorNote}</span>}
                                    </div>
                                ) : (
                                    <div className="flex flex-col">
                                        <span className="text-xs font-bold text-[#73CB64] flex items-center gap-1">
                                            <FaMoneyBillWave size={10}/> Received from Patient
                                        </span>
                                        <span className="text-[10px] text-gray-500 mt-0.5">Via {tx.paymentMode} {tx.note ? `- ${tx.note}` : ''}</span>
                                    </div>
                                )}
                            </td>

                            {/* AMOUNT COLUMN */}
                            <td className="px-4 py-3 text-right font-bold text-sm">
                                {isDocPay ? (
                                    <span className="text-red-500">- {parseFloat(tx.doctorAmount).toFixed(2)}</span>
                                ) : (
                                    <span className="text-[#73CB64]">+ {parseFloat(tx.amount).toFixed(2)}</span>
                                )}
                            </td>

                            <td className="px-4 py-3">
                              <div className="flex justify-center gap-2">
                                <button onClick={() => handlePrintReceipt(tx)} className="text-blue-500 hover:text-blue-700 transition" title="Print"><FaPrint /></button>
                                <button onClick={() => initiateDelete(tx.id)} className="text-red-500 hover:text-red-700 transition" title="Delete"><FaTrash /></button>
                              </div>
                            </td>
                          </tr>
                       );
                    }) : (
                      <tr><td colSpan="5" className="px-4 py-8 text-center text-gray-400 italic">No transactions found.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

          </div>
        </div>
      </div>
      
      {/* Print Layout */}
      {showPrintPreview && printData && (
        <div className="payment-history-print-preview">
          <PrintBillLayoutA5
              className="payment-history-print-content"
              title={printData.title}
              billInfo={printData.billInfo}
              table={printData.table}
              summary={printData.summary}
              footer={printData.signatureFooter}
          />
        </div>
      )}

      {/* Confirm Modal */}
      <ConfirmActionModal
        isOpen={confirmDelete.show}
        onClose={() => setConfirmDelete({ show: false, paymentId: null })}
        onConfirm={executeDelete}
        ids={[confirmDelete.paymentId]}
        entityName="payment"
        title="Delete Payment"
        message="Are you sure you want to delete this payment record? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
      />
    </>
  );
};

export default HospitalAddPaymentModal;